/* eslint-disable @typescript-eslint/no-explicit-any --
   Dynamic SVG port rendering with variable device data shapes.
   Full typing requires the device type profile system (roadmap). */
// ═══════════════════════════════════════════════════════════
// DEVICE MODULE REGISTRY
// Known products render photo-realistic SVG front panels.
// Unknown products render configurable generic boxes.
// Each module defines: front face, back face, port positions, U size.
// Modules are keyed by MPN or model string from the product catalog.
// ═══════════════════════════════════════════════════════════

import type { ReactNode } from 'react'

const TYPE_COLORS: Record<string, string> = {
  camera: 'var(--color-camera)',
  networking: 'var(--color-networking)',
  access: 'var(--color-access)',
  phone: 'var(--color-cable)',
  uplink: 'var(--color-infra)',
}

export interface DeviceModuleDevice {
  id: string
  name: string
  nameShort?: string
  model: string
  status: string
  ip?: string
  driveStates?: Record<number, { present?: boolean; activity?: boolean; io?: boolean; status?: string }>
  health?: string
  serviceTag?: string
  power?: boolean
  psus?: { online?: boolean }[]
  ports?: Record<number, any>
}

export interface PortConnection {
  deviceType?: string
  offline?: boolean
}

export interface DeviceModuleRenderProps {
  ports?: Record<number, PortConnection>
  hovPort?: number | string | null
  selPort?: number | string | null
  onHover?: (port: number | string | null) => void
  onClick?: (port: number | string | null) => void
  poeData?: Record<number, number>
  device?: DeviceModuleDevice
}

export interface DeviceModule {
  name?: string
  sizeU?: number
  frontPorts?: number
  rearPorts?: number
  sfpPorts?: number
  renderFront: (w: number, h: number, props: DeviceModuleRenderProps) => ReactNode
  renderBack: (w: number, h: number, props: DeviceModuleRenderProps) => ReactNode
  getPortPosition?: (portNum: number, w: number, h: number) => { x: number; y: number }
}

export const DEVICE_MODULES: Record<string, DeviceModule> = {
  // ── VERTICAL CABLE 24-PORT PATCH PANEL ──
  'VC-042-024': {
    name: '24-Port Cat6 Patch Panel',
    sizeU: 1,
    frontPorts: 24,
    renderFront: (w, h, { ports, hovPort, selPort, onHover, onClick }) => {
      const elements: ReactNode[] = []
      // Panel body
      elements.push(
        <rect key="body" x={0} y={0} width={w} height={h} rx={2} fill="#1e2024" stroke="#3a3d46" strokeWidth={0.5} />,
        <rect key="amber-top" x={0} y={0} width={w} height={1} fill="var(--accent)" opacity={0.5} rx={1} />,
        <rect key="top" x={0} y={1} width={w} height={2} fill="#282a2e" rx={1} />,
        <text key="label" x={w / 2} y={h / 2 - 12} textAnchor="middle" fontSize={5} fill="var(--text-3)" fontFamily="var(--font-mono)" fontWeight={700}>VERTICAL CABLE</text>,
      )
      // 24 ports in 2 rows of 12
      for (let i = 0; i < 24; i++) {
        const row = Math.floor(i / 12)
        const col = i % 12
        const px = 28 + col * (w - 56) / 11.5
        const py = h / 2 - 4 + row * 14
        const conn = ports?.[i + 1]
        const color = conn ? TYPE_COLORS[conn.deviceType || ''] || 'var(--accent)' : '#1e2028'
        const isHov = hovPort === i + 1
        const isSel = selPort === i + 1
        elements.push(
          <g key={`port-${i}`} onMouseEnter={() => onHover?.(i + 1)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(i + 1)} style={{ cursor: conn ? 'pointer' : 'default' }}>
            <rect x={px - 5} y={py - 4} width={10} height={9} rx={1.5}
              fill={conn ? color + '40' : '#141518'}
              stroke={isSel ? 'var(--accent)' : isHov && conn ? color : '#3a3d46'}
              strokeWidth={isSel ? 1.5 : isHov ? 1 : 0.5} />
            <text x={px} y={py + 3} textAnchor="middle" fontSize={5} fill={conn ? color : 'var(--text-3)'} fontFamily="var(--font-mono)" fontWeight={700}>{i + 1}</text>
            {conn && <circle cx={px + 4} cy={py - 3} r={2} fill={conn.offline ? 'var(--err)' : 'var(--ok)'}>
              {!conn.offline && <animate attributeName="opacity" values="1;0.4;1" dur="3s" repeatCount="indefinite" />}
            </circle>}
          </g>,
        )
      }
      return <g>{elements}</g>
    },
    renderBack: (w, h) => {
      const elements: ReactNode[] = [
        <rect key="body" x={0} y={0} width={w} height={h} rx={2} fill="#111" stroke="#333" strokeWidth={0.5} />,
      ]
      for (let i = 0; i < 24; i++) {
        const row = Math.floor(i / 12)
        const col = i % 12
        const px = 28 + col * (w - 56) / 11.5
        const py = h / 2 - 2 + row * 12
        elements.push(
          <rect key={`rear-${i}`} x={px - 4} y={py - 3} width={8} height={7} rx={1} fill="#1a1a1a" stroke="#2a2d35" strokeWidth={0.3} />,
        )
      }
      elements.push(
        <text key="punchdown" x={w / 2} y={h - 3} textAnchor="middle" fontSize={4} fill="#2a2d35" fontFamily="var(--font-mono)">110 PUNCHDOWN</text>,
      )
      return <g>{elements}</g>
    },
    getPortPosition: (portNum, w, h) => {
      const i = portNum - 1
      const row = Math.floor(i / 12)
      const col = i % 12
      return { x: 28 + col * (w - 56) / 11.5, y: h / 2 - 4 + row * 14 + 4.5 }
    },
  },

  // ── UNIFI 24-PORT PoE SWITCH ──
  'USW-24-POE': {
    name: 'USW-24-PoE',
    sizeU: 1,
    frontPorts: 24,
    sfpPorts: 2,
    renderFront: (w, h, { ports, hovPort, selPort, onHover, onClick, poeData, device: _device }) => {
      const elements: ReactNode[] = []
      // Switch body
      elements.push(
        <defs key="defs">
          <linearGradient id="switchBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e2024" />
            <stop offset="50%" stopColor="#1a1c20" />
            <stop offset="100%" stopColor="#161820" />
          </linearGradient>
        </defs>,
        <rect key="body" x={0} y={0} width={w} height={h} rx={2} fill="url(#switchBody)" stroke="#3a3d46" strokeWidth={0.5} />,
        <rect key="amber-top" x={0} y={0} width={w} height={1} fill="var(--accent)" opacity={0.5} rx={1} />,
        <circle key="logo-ring" cx={14} cy={h / 2} r={4} fill="none" stroke="#2563eb30" strokeWidth={0.5} />,
        <circle key="logo-dot" cx={14} cy={h / 2} r={1.5} fill="#2563eb">
          <animate attributeName="opacity" values="1;0.4;1" dur="4s" repeatCount="indefinite" />
        </circle>,
        <text key="model" x={24} y={8} fontSize={4} fill="#3a3d46" fontFamily="var(--font-mono)" fontWeight={600}>USW-24-PoE</text>,
      )
      // 24 ports in 2 rows of 12
      for (let i = 0; i < 24; i++) {
        const row = Math.floor(i / 12)
        const col = i % 12
        const px = 30 + col * ((w - 80) / 11.5)
        const py = h / 2 - 5 + row * 13
        const conn = ports?.[i + 1]
        const color = conn ? TYPE_COLORS[conn.deviceType || ''] || 'var(--accent)' : '#1e2028'
        const isHov = hovPort === i + 1
        const isSel = selPort === i + 1
        const poe = poeData?.[i + 1]
        elements.push(
          <g key={`port-${i}`} onMouseEnter={() => onHover?.(i + 1)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(i + 1)} style={{ cursor: conn ? 'pointer' : 'default' }}>
            <rect x={px - 5} y={py - 4.5} width={10} height={9.5} rx={1}
              fill={conn ? color + '40' : '#141518'}
              stroke={isSel ? 'var(--accent)' : isHov && conn ? color + '99' : '#3a3d46'}
              strokeWidth={isSel ? 1.5 : isHov ? 1 : 0.4} />
            <rect x={px - 2.5} y={py - 5} width={5} height={1.5} rx={0.5} fill={conn ? color + '60' : '#1a1a1a'} />
            <text x={px} y={py + 2.5} textAnchor="middle" fontSize={5} fill={conn ? color : 'var(--text-3)'} fontFamily="var(--font-mono)" fontWeight={800}>{i + 1}</text>
            {conn && <circle cx={px + 4} cy={py - 3.5} r={2} fill={conn.offline ? 'var(--err)' : 'var(--ok)'}>
              {!conn.offline && <animate attributeName="opacity" values="1;0.3;1" dur={`${2 + Math.random() * 2}s`} repeatCount="indefinite" />}
            </circle>}
            {poe && <rect x={px - 4} y={py + 5.5} width={8 * Math.min(poe / 30, 1)} height={1} rx={0.5} fill={poe > 20 ? 'var(--err)' : poe > 12 ? 'var(--color-power)' : 'var(--ok)'} opacity={0.6} />}
          </g>,
        )
      }
      // SFP ports
      for (let i = 0; i < 2; i++) {
        elements.push(
          <g key={`sfp-${i}`}>
            <rect x={w - 32 + i * 14} y={h / 2 - 5} width={11} height={10} rx={1.5} fill="#0a0a0c" stroke="#2a2d35" strokeWidth={0.4} strokeDasharray="2 1" />
            <text x={w - 26.5 + i * 14} y={h / 2 + 2} textAnchor="middle" fontSize={3} fill="#2a2d35" fontFamily="var(--font-mono)" fontWeight={700}>SFP</text>
          </g>,
        )
      }
      return <g>{elements}</g>
    },
    renderBack: (w, h) => (
      <g>
        <rect x={0} y={0} width={w} height={h} rx={2} fill="#111" stroke="#2a2d35" strokeWidth={0.5} />
        <rect x={8} y={h / 2 - 4} width={16} height={8} rx={1} fill="#0a0a0c" stroke="#222" strokeWidth={0.3} />
        <text x={16} y={h / 2 + 1.5} textAnchor="middle" fontSize={3} fill="#2a2d35" fontFamily="var(--font-mono)">POWER</text>
        <text x={w / 2} y={h - 3} textAnchor="middle" fontSize={3.5} fill="#2a2d35" fontFamily="var(--font-mono)">USW-24-PoE · 400W PoE Budget</text>
        {Array.from({ length: 20 }, (_, i) => <circle key={i} cx={40 + i * 22} cy={h / 2} r={2} fill="none" stroke="#1a1a1a" strokeWidth={0.3} />)}
      </g>
    ),
    getPortPosition: (portNum, w, h) => {
      const i = portNum - 1
      const row = Math.floor(i / 12)
      const col = i % 12
      return { x: 30 + col * ((w - 80) / 11.5), y: h / 2 - 5 + row * 13 + 4.5 }
    },
  },

  // ── DELL POWEREDGE R940 ──
  'DELL-R940': {
    name: 'Dell PowerEdge R940',
    sizeU: 3,
    frontPorts: 24,
    renderFront: (w, h, { device, ports: _ports = {}, hovPort, selPort, onHover, onClick }) => {
      const driveStates = (device as any)?.driveStates || {}
      const health = (device as any)?.health || 'ok'
      const infoText = (device as any)?.serviceTag || 'R940'
      const powerOn = (device as any)?.power !== false

      const driveAreaX = 72
      const driveAreaY = 18
      const cols = 6
      const rows = 4
      const gapX = 6
      const gapY = 6
      const bayW = (w - 144 - gapX * (cols - 1)) / cols
      const bayH = (h - 34 - gapY * (rows - 1)) / rows

      const healthColor =
        health === 'critical' ? 'var(--err)' :
        health === 'warn' ? 'var(--color-power)' :
        'var(--ok)'

      const elements: ReactNode[] = []
      elements.push(
        <defs key="r940-defs">
          <linearGradient id="r940-front-body-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e2024" />
            <stop offset="100%" stopColor="#161820" />
          </linearGradient>
          <linearGradient id="r940-drive-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#191c22" />
            <stop offset="100%" stopColor="#0b0c0f" />
          </linearGradient>
          <linearGradient id="r940-panel-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#101216" />
            <stop offset="100%" stopColor="#0a0b0e" />
          </linearGradient>
          <pattern id="r940-top-vent" width="6" height="6" patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill="transparent" />
            <rect x="0" y="2.5" width="3.6" height="1" rx="0.5" fill="#20242b" />
          </pattern>
        </defs>,
      )

      // Body
      elements.push(
        <rect key="body" x={0} y={0} width={w} height={h} rx={4} fill="url(#r940-front-body-grad)" stroke="#3a3d46" strokeWidth={1} />,
        <rect key="amber-top" x={0} y={0} width={w} height={1} fill="var(--accent)" opacity={0.5} rx={1} />,
        <rect key="vents" x={10} y={6} width={w - 20} height={10} fill="url(#r940-top-vent)" opacity={0.9} />,
        <line key="line-top" x1={0} y1={14} x2={w} y2={14} stroke="#22262d" strokeWidth={0.8} />,
        <line key="line-bot" x1={0} y1={h - 10} x2={w} y2={h - 10} stroke="#1f232a" strokeWidth={0.8} />,
      )

      // Rack ears
      elements.push(
        <rect key="ear-l" x={0} y={0} width={12} height={h} fill="#0b0c0f" />,
        <rect key="ear-r" x={w - 12} y={0} width={12} height={h} fill="#0b0c0f" />,
      )
      ;[18, h / 2, h - 18].forEach((yy, i) => {
        elements.push(
          <circle key={`ear-screw-l-${i}`} cx={6} cy={yy} r={2.3} fill="#050607" stroke="#2b313a" strokeWidth={0.8} />,
          <circle key={`ear-screw-r-${i}`} cx={w - 6} cy={yy} r={2.3} fill="#050607" stroke="#2b313a" strokeWidth={0.8} />,
        )
      })

      // Left info panel
      elements.push(
        <g key="left-panel" transform="translate(18,18)">
          <rect x={0} y={0} width={40} height={h - 36} rx={2} fill="url(#r940-panel-grad)" stroke="#232933" strokeWidth={0.8} />
          <rect x={6} y={6} width={7} height={h - 58} rx={1.5} fill="#0b0d10" stroke="#2a2f37" strokeWidth={0.6} />
          <rect x={7.5} y={12} width={4} height={h - 70} rx={1} fill="#f4c542" opacity={0.9} />
          <text x={9.5} y={h - 48} textAnchor="middle" fontSize={3.2} fill="#111" fontFamily="var(--font-mono)" fontWeight={700} transform={`rotate(-90 9.5 ${h - 48})`}>INFO</text>
          <rect x={18} y={8} width={16} height={10} rx={1.5} fill="#11151b" stroke="#314152" strokeWidth={0.7} />
          <text x={26} y={14.5} textAnchor="middle" fontSize={4.2} fill={healthColor} fontFamily="var(--font-mono)" fontWeight={700}>{infoText}</text>
          <circle cx={26} cy={28} r={4.1} fill="#0b0d10" stroke="#305f9b" strokeWidth={0.9} />
          <circle cx={26} cy={28} r={2.2} fill="#3b82f6" opacity={0.9}><animate attributeName="opacity" values="1;0.35;1" dur="2.6s" repeatCount="indefinite" /></circle>
          <text x={26} y={37.5} textAnchor="middle" fontSize={2.8} fill="#6f8196" fontFamily="var(--font-mono)">ID</text>
          <circle cx={26} cy={49} r={4.5} fill="#0d1014" stroke="#2f3843" strokeWidth={0.9} />
          <circle cx={26} cy={49} r={1.6} fill={healthColor}><animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" /></circle>
          <text x={26} y={58.5} textAnchor="middle" fontSize={2.6} fill="#728091" fontFamily="var(--font-mono)">SYS</text>
          <circle cx={26} cy={72} r={5.2} fill="#0a0d10" stroke="#2e6c99" strokeWidth={0.9} />
          <path d="M23.8 72a2.2 2.2 0 0 1 4.4 0M24.7 70.7a1.35 1.35 0 0 1 2.6 0" fill="none" stroke="#60a5fa" strokeWidth={0.9} strokeLinecap="round" />
          <circle cx={26} cy={73.3} r={0.7} fill="#60a5fa" />
          <text x={26} y={83} textAnchor="middle" fontSize={2.5} fill="#728091" fontFamily="var(--font-mono)">NFC</text>
        </g>,
      )

      // Right panel (power, USB, VGA, iDRAC)
      elements.push(
        <g key="right-panel" transform={`translate(${w - 58},18)`}>
          <rect x={0} y={0} width={40} height={h - 36} rx={2} fill="url(#r940-panel-grad)" stroke="#232933" strokeWidth={0.8} />
          <circle cx={20} cy={13} r={6} fill="#0b0d10" stroke={powerOn ? '#3b82f6' : '#3a3f47'} strokeWidth={1.2} />
          <path d="M20 9.6v4.2M17 11.8a4.1 4.1 0 1 0 6 0" fill="none" stroke={powerOn ? '#60a5fa' : '#67707d'} strokeWidth={1.1} strokeLinecap="round" />
          {powerOn && <circle cx={20} cy={13} r={7.4} fill="none" stroke="#60a5fa" strokeWidth={0.8} opacity={0.5}><animate attributeName="opacity" values="0.45;0.15;0.45" dur="2.8s" repeatCount="indefinite" /></circle>}
          <rect x={8} y={26} width={10} height={6.5} rx={0.8} fill="#0a0b0d" stroke="#314152" strokeWidth={0.7} />
          <rect x={9.2} y={27.1} width={7.6} height={4.3} fill="#0f1720" />
          <rect x={18} y={27.6} width={1.4} height={3.2} rx={0.4} fill="#60a5fa" />
          <text x={13} y={37.8} textAnchor="middle" fontSize={2.5} fill="#728091" fontFamily="var(--font-mono)">USB</text>
          <rect x={22} y={25.5} width={11.5} height={8} rx={0.8} fill="#111319" stroke="#36516e" strokeWidth={0.7} />
          <rect x={24} y={27.2} width={7.5} height={4.6} fill="#1d3b5e" opacity={0.95} />
          <text x={27.8} y={37.8} textAnchor="middle" fontSize={2.5} fill="#728091" fontFamily="var(--font-mono)">VGA</text>
          <rect x={8} y={45} width={10} height={6.5} rx={0.8} fill="#0a0b0d" stroke="#314152" strokeWidth={0.7} />
          <rect x={9.2} y={46.1} width={7.6} height={4.3} fill="#0f1720" />
          <rect x={18} y={46.6} width={1.4} height={3.2} rx={0.4} fill="#60a5fa" />
          <text x={13} y={56.8} textAnchor="middle" fontSize={2.5} fill="#728091" fontFamily="var(--font-mono)">USB</text>
          <rect x={22} y={44.7} width={11.8} height={7.1} rx={1} fill="#0b0d10" stroke="#2f3843" strokeWidth={0.7} />
          <rect x={23.3} y={46} width={9.2} height={4.5} rx={0.8} fill="#0e1520" stroke="#376894" strokeWidth={0.5} />
          <circle cx={32.2} cy={45.6} r={0.8} fill="#3b82f6"><animate attributeName="opacity" values="1;0.3;1" dur="2.2s" repeatCount="indefinite" /></circle>
          <text x={27.9} y={56.8} textAnchor="middle" fontSize={2.3} fill="#728091" fontFamily="var(--font-mono)">iDRAC</text>
          <circle cx={20} cy={h - 48} r={4} fill="#0b0d10" stroke="#315f9a" strokeWidth={0.8} />
          <circle cx={20} cy={h - 48} r={1.9} fill="#3b82f6"><animate attributeName="opacity" values="1;0.4;1" dur="2.5s" repeatCount="indefinite" /></circle>
          <text x={20} y={h - 38.5} textAnchor="middle" fontSize={2.6} fill="#728091" fontFamily="var(--font-mono)">ID</text>
        </g>,
      )

      // Drive bays (24 bays, 6 cols x 4 rows)
      for (let i = 0; i < 24; i++) {
        const col = i % cols
        const row = Math.floor(i / cols)
        const x = driveAreaX + col * (bayW + gapX)
        const y = driveAreaY + row * (bayH + gapY)
        const bayNum = i + 1
        const d = driveStates[bayNum] || {}
        const present = d.present !== false
        const activity = d.activity ?? !!d.io
        const state = d.status || (present ? 'ok' : 'empty')
        const statusColor =
          state === 'failed' ? 'var(--err)' :
          state === 'rebuild' ? 'var(--color-power)' :
          state === 'empty' ? '#374151' :
          'var(--ok)'

        const isHov = hovPort === `bay-${bayNum}`
        const isSel = selPort === `bay-${bayNum}`

        elements.push(
          <g key={`bay-${bayNum}`}
            onMouseEnter={() => onHover?.(`bay-${bayNum}`)}
            onMouseLeave={() => onHover?.(null)}
            onClick={() => onClick?.(`bay-${bayNum}`)}
            style={{ cursor: 'pointer' }}
          >
            <rect x={x} y={y} width={bayW} height={bayH} rx={1.8}
              fill="url(#r940-drive-grad)"
              stroke={isSel ? 'var(--accent)' : isHov ? 'var(--color-infra)' : '#242932'}
              strokeWidth={isSel ? 1.4 : isHov ? 1 : 0.8} />
            <rect x={x + 1.4} y={y + 1.4} width={bayW - 2.8} height={bayH - 2.8} rx={1.2} fill="#0a0b0d" stroke="#1a1d22" strokeWidth={0.5} />
            <rect x={x + bayW * 0.44} y={y + 4} width={bayW * 0.12} height={bayH - 8} rx={1} fill="#171a20" />
            <rect x={x + 3} y={y + bayH - 7.8} width={bayW - 6} height={4} rx={0.8} fill="#13171c" />
            <text x={x + bayW / 2} y={y + bayH - 4.7} textAnchor="middle" fontSize={3.1} fill="#7d8796" fontFamily="var(--font-mono)" fontWeight={700}>{bayNum}</text>
            {present && <circle cx={x + 4.3} cy={y + 4.5} r={1.2} fill="#2563eb">
              {activity && <animate attributeName="opacity" values="1;0.25;1" dur="0.9s" repeatCount="indefinite" />}
            </circle>}
            <circle cx={x + bayW - 4.3} cy={y + 4.5} r={1.2} fill={statusColor}>
              {present && state !== 'empty' && <animate attributeName="opacity" values="1;0.55;1" dur="2.4s" repeatCount="indefinite" />}
            </circle>
            <rect x={x + bayW - 5.2} y={y + bayH / 2 - 5} width={2.8} height={10} rx={0.8} fill="#20242b" />
          </g>,
        )
      }

      // Dell branding
      elements.push(
        <text key="dell-brand" x={w - 34} y={h - 15} textAnchor="end" fontSize={6} fill="#9aa4b2" opacity={0.75} fontFamily="var(--font-mono)" fontWeight={700}>DELL</text>,
      )

      return <g>{elements}</g>
    },
    renderBack: (w, h, { device, ports = {}, hovPort, selPort, onHover, onClick }) => {
      const portData = (n: number) => (ports as any)?.[n] || (device as any)?.ports?.[n] || null

      const elements: ReactNode[] = []
      elements.push(
        <defs key="r940-back-defs">
          <linearGradient id="r940-back-body-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#171a1f" />
            <stop offset="100%" stopColor="#0b0c10" />
          </linearGradient>
          <pattern id="r940-back-vent" width="6" height="6" patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill="transparent" />
            <rect x="0.8" y="1" width="3.5" height="1" rx="0.5" fill="#2a313a" />
            <rect x="1.8" y="3.5" width="3.5" height="1" rx="0.5" fill="#2a313a" />
          </pattern>
        </defs>,
      )

      // Body + ears
      elements.push(
        <rect key="body" x={0} y={0} width={w} height={h} rx={4} fill="url(#r940-back-body-grad)" stroke="#242932" strokeWidth={1} />,
        <rect key="ear-l" x={0} y={0} width={12} height={h} fill="#0b0c0f" />,
        <rect key="ear-r" x={w - 12} y={0} width={12} height={h} fill="#0b0c0f" />,
      )
      ;[18, h / 2, h - 18].forEach((yy, i) => {
        elements.push(
          <circle key={`rear-ear-l-${i}`} cx={6} cy={yy} r={2.3} fill="#050607" stroke="#2b313a" strokeWidth={0.8} />,
          <circle key={`rear-ear-r-${i}`} cx={w - 6} cy={yy} r={2.3} fill="#050607" stroke="#2b313a" strokeWidth={0.8} />,
        )
      })

      // Left IO panel (fan, USB, ID, VGA)
      elements.push(
        <g key="io-left" transform="translate(18,18)">
          <rect x={0} y={0} width={44} height={56} rx={2} fill="#11141a" stroke="#232933" strokeWidth={0.8} />
          <circle cx={14} cy={28} r={11} fill="#0b0d10" stroke="#38414d" strokeWidth={0.8} />
          {Array.from({ length: 6 }, (_, i) => (
            <path key={`fan-${i}`} d="M14 28 L14 18 A10 10 0 0 1 21.5 21.5 Z" fill="#2a313a" transform={`rotate(${i * 60} 14 28)`} />
          ))}
          <circle cx={14} cy={28} r={3.2} fill="#059669"><animate attributeName="opacity" values="1;0.35;1" dur="2.4s" repeatCount="indefinite" /></circle>
          <rect x={28} y={16} width={10} height={6.2} rx={0.8} fill="#0b0d10" stroke="#31506c" strokeWidth={0.7} />
          <rect x={29} y={17} width={7.8} height={4.1} fill="#102030" />
          <rect x={38} y={17.5} width={1.2} height={3.1} rx={0.4} fill="#60a5fa" />
          <rect x={28} y={26} width={10} height={6.2} rx={0.8} fill="#0b0d10" stroke="#31506c" strokeWidth={0.7} />
          <rect x={29} y={27} width={7.8} height={4.1} fill="#102030" />
          <rect x={38} y={27.5} width={1.2} height={3.1} rx={0.4} fill="#60a5fa" />
          <circle cx={33} cy={39.5} r={3.6} fill="#0a0b0d" stroke="#2d3641" strokeWidth={0.7} />
          <circle cx={33} cy={39.5} r={1.5} fill="#3b82f6"><animate attributeName="opacity" values="1;0.4;1" dur="2.8s" repeatCount="indefinite" /></circle>
          <rect x={27} y={47} width={12} height={7.5} rx={0.8} fill="#111319" stroke="#36516e" strokeWidth={0.7} />
          <rect x={28.6} y={48.4} width={8.8} height={4.5} fill="#1d3b5e" />
        </g>,
      )

      // Center fans/heatsink area
      elements.push(
        <g key="center-area" transform="translate(84,12)">
          <rect x={0} y={0} width={170} height={h - 24} rx={2} fill="#11141a" stroke="#232933" strokeWidth={0.8} />
          {Array.from({ length: 6 }, (_, i) => (
            <g key={`vent-${i}`}>
              <rect x={12 + i * 25} y={8} width={14} height={h - 40} fill="url(#r940-back-vent)" opacity={0.95} />
              <rect x={10 + i * 25} y={h - 24} width={18} height={4} rx={1} fill="#1d222a" />
            </g>
          ))}
          <rect x={148} y={18} width={16} height={h - 48} fill="url(#r940-back-vent)" opacity={0.95} />
          <rect x={148} y={h - 26} width={16} height={4} rx={1} fill="#1d222a" />
        </g>,
      )

      // NIC ports (4x RJ45 + 1x iDRAC)
      elements.push(
        <g key="nic-ports" transform="translate(262,18)">
          <rect x={0} y={0} width={102} height={56} rx={2} fill="#11141a" stroke="#232933" strokeWidth={0.8} />
          <text x={8} y={8} fontSize={3.2} fill="#7c8796" fontFamily="var(--font-mono)">NDC</text>
          {[1, 2, 3, 4].map((n, i) => {
            const conn = portData(n)
            const isH = hovPort === n
            const isS = selPort === n
            const color = conn?.color || '#2563eb'
            const online = conn ? !conn.offline : false
            const px = 11 + i * 22
            const py = 26
            return (
              <g key={`nic-${n}`} onMouseEnter={() => onHover?.(n)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(n)} style={{ cursor: 'pointer' }}>
                <rect x={px} y={py} width={16} height={12} rx={1.2}
                  fill={conn ? color + '18' : '#0a0a0c'}
                  stroke={isS ? 'var(--accent)' : isH ? (conn ? color : 'var(--color-infra)') : '#2a2f37'}
                  strokeWidth={isS ? 1.4 : isH ? 1 : 0.8} />
                <rect x={px + 3.4} y={py - 1.6} width={9.2} height={2.2} rx={0.6} fill={conn ? color + '45' : '#1a1a1a'} />
                <rect x={px + 2.2} y={py + 2.2} width={11.6} height={6.4} rx={0.6} fill="#0a0c0e" stroke="#20242b" strokeWidth={0.4} />
                <text x={px + 8} y={py + 16.5} textAnchor="middle" fontSize={3} fill={conn ? color : '#67707d'} fontFamily="var(--font-mono)" fontWeight={700}>{n}</text>
                {conn && <circle cx={px + 14} cy={py + 2.3} r={1.1} fill={online ? 'var(--ok)' : 'var(--err)'}><animate attributeName="opacity" values="1;0.35;1" dur="2.5s" repeatCount="indefinite" /></circle>}
              </g>
            )
          })}
          {(() => {
            const n = 5
            const conn = portData(n)
            const isH = hovPort === n
            const isS = selPort === n
            const color = conn?.color || '#3b82f6'
            const online = conn ? !conn.offline : false
            const px = 77
            const py = 26
            return (
              <g key="idrac-port" onMouseEnter={() => onHover?.(n)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(n)} style={{ cursor: 'pointer' }}>
                <rect x={px} y={py} width={16} height={12} rx={1.2}
                  fill={conn ? color + '18' : '#0a0a0c'}
                  stroke={isS ? 'var(--accent)' : isH ? (conn ? color : 'var(--color-infra)') : '#2a2f37'}
                  strokeWidth={isS ? 1.4 : isH ? 1 : 0.8} />
                <rect x={px + 3.4} y={py - 1.6} width={9.2} height={2.2} rx={0.6} fill={conn ? color + '45' : '#1a1a1a'} />
                <rect x={px + 2.2} y={py + 2.2} width={11.6} height={6.4} rx={0.6} fill="#0a0c0e" stroke="#20242b" strokeWidth={0.4} />
                <text x={px + 8} y={py + 16.5} textAnchor="middle" fontSize={2.8} fill={conn ? color : '#67707d'} fontFamily="var(--font-mono)" fontWeight={700}>IDR</text>
                {conn && <circle cx={px + 14} cy={py + 2.3} r={1.1} fill={online ? 'var(--ok)' : 'var(--err)'}><animate attributeName="opacity" values="1;0.35;1" dur="2.5s" repeatCount="indefinite" /></circle>}
              </g>
            )
          })()}
        </g>,
      )

      // Right IO panel (VGA, serial, USB, ID)
      elements.push(
        <g key="io-right" transform="translate(370,18)">
          <rect x={0} y={0} width={34} height={56} rx={2} fill="#11141a" stroke="#232933" strokeWidth={0.8} />
          <rect x={6} y={8} width={18} height={10} rx={1} fill="#111319" stroke="#36516e" strokeWidth={0.7} />
          <rect x={8.8} y={10.1} width={12.4} height={5.6} fill="#1d3b5e" />
          {Array.from({ length: 3 }, (_, r) =>
            Array.from({ length: 5 }, (__, c) => (
              <circle key={`vga-${r}-${c}`} cx={10.4 + c * 2.2} cy={11.5 + r * 1.8} r={0.32} fill="#c0d5f2" />
            ))
          ).flat()}
          <rect x={7} y={24} width={16} height={8} rx={1} fill="#0a0b0d" stroke="#2d3641" strokeWidth={0.7} />
          {Array.from({ length: 9 }, (_, i) => (
            <circle key={`ser-${i}`} cx={9.2 + (i % 5) * 2.7} cy={26.2 + Math.floor(i / 5) * 2.3} r={0.35} fill="#a9b2bf" />
          ))}
          <rect x={8} y={38} width={10} height={6.2} rx={0.8} fill="#0b0d10" stroke="#31506c" strokeWidth={0.7} />
          <rect x={9} y={39} width={7.8} height={4.1} fill="#102030" />
          <rect x={18} y={39.5} width={1.2} height={3.1} rx={0.4} fill="#60a5fa" />
          <circle cx={26.5} cy={41} r={3.3} fill="#0a0c0f" stroke="#315f9a" strokeWidth={0.7} />
          <circle cx={26.5} cy={41} r={1.3} fill="#3b82f6"><animate attributeName="opacity" values="1;0.35;1" dur="2.6s" repeatCount="indefinite" /></circle>
        </g>,
      )

      // PSUs
      ;[0, 1].forEach((i) => {
        const x = 408 + i * 18
        const psuOn = (device as any)?.psus?.[i]?.online !== false
        elements.push(
          <g key={`psu-${i}`} transform={`translate(${x},18)`}>
            <rect x={0} y={0} width={16} height={56} rx={1.5} fill="#12151b" stroke="#2a313a" strokeWidth={0.8} />
            <rect x={2.2} y={8} width={11.6} height={22} rx={1} fill="#0b0d10" stroke="#313844" strokeWidth={0.6} />
            <path d="M4.5 12 h7 l1.4 3.5 v7 l-1.4 3.5 h-7 l-1.2-3.5 v-7z" fill="#20242b" />
            <rect x={4} y={35} width={8} height={9} rx={0.8} fill="#20242b" />
            <circle cx={12.2} cy={48.5} r={1.2} fill={psuOn ? 'var(--ok)' : 'var(--err)'}><animate attributeName="opacity" values="1;0.35;1" dur="2.4s" repeatCount="indefinite" /></circle>
            <rect x={3.2} y={46.5} width={5.4} height={4.5} rx={0.7} fill="#171b21" />
          </g>,
        )
      })

      return <g>{elements}</g>
    },
    getPortPosition: (portNum, w, h) => {
      const portMap: Record<number, { x: number; y: number }> = {
        1: { x: 281, y: 50 },
        2: { x: 303, y: 50 },
        3: { x: 325, y: 50 },
        4: { x: 347, y: 50 },
        5: { x: 369, y: 50 },
      }
      return portMap[portNum] || { x: w - 70, y: h / 2 }
    },
  },

}

const TYPE_COLORS_SW: Record<string, string> = {
  camera: "var(--color-camera)",
  networking: "var(--color-networking)",
  access: "var(--color-access)",
  phone: "var(--color-cable)",
  uplink: "var(--color-infra)",
  power: "var(--color-power)",
}

interface SwitchModuleConfig {
  mpn: string
  name: string
  rj45Count: number
  sfpCount?: number
  sfpPlusCount?: number
  sizeU?: number
  poePorts?: number
  poePlusPorts?: number
  poePlusPlusPorts?: number
  lcd?: boolean
  depthClass?: "standard" | "pro"
  etherlighting?: boolean
}

function makeUniFiSwitchModule(config: SwitchModuleConfig): DeviceModule {
  const { mpn, name, rj45Count, sfpCount = 0, sfpPlusCount = 0, sizeU = 1, poePorts = 0, poePlusPorts = 0, poePlusPlusPorts = 0, lcd = true, depthClass = "standard", etherlighting = false } = config
  const totalFrontPorts = rj45Count + sfpCount + sfpPlusCount
  const is48 = rj45Count >= 48
  const portW = 12
  const portH = 9.5
  const gap = 3.2
  const left = 18
  const rowY = 20

  const getFrontPortPosition = (portNum: number, w: number, _h: number) => {
    if (portNum <= rj45Count) {
      const idx = portNum - 1
      const row = is48 ? Math.floor(idx / 24) : Math.floor(idx / 12)
      const col = is48 ? idx % 24 : idx % 12
      return { x: left + col * (portW + gap) + portW / 2, y: rowY + row * 14 + portH / 2 }
    }
    const sfpIndex = portNum - rj45Count - 1
    const sfpXStart = is48 ? w - 66 : w - 42
    const sfpY = is48 ? 27 : 20
    return { x: sfpXStart + sfpIndex * 14 + 6, y: sfpY + 5 }
  }

  const getPoEClass = (portNum: number) => {
    if (poePlusPlusPorts > 0) {
      const start = poePorts + poePlusPorts + 1
      if (portNum >= start && portNum < start + poePlusPlusPorts) return "++"
    }
    if (poePlusPorts > 0) {
      const start = poePorts + 1
      if (portNum >= start && portNum < start + poePlusPorts) return "+"
    }
    if (poePorts > 0 && portNum <= poePorts) return "poe"
    return null
  }

  return {
    name,
    sizeU,
    frontPorts: totalFrontPorts,
    rearPorts: 2,
    renderFront: (w: number, h: number, { device, ports = {}, hovPort, selPort, onHover, onClick, poeData: _poeData }: DeviceModuleRenderProps) => {
      const bodyStroke = etherlighting ? "#335a7c" : "#28303a"
      const getConn = (n: number) => (ports as any)?.[n] || (device as any)?.ports?.[n] || null
      return (
        <g>
          <defs>
            <linearGradient id={`${mpn}-front-grad`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e2024" />
              <stop offset="100%" stopColor="#161820" />
            </linearGradient>
            <linearGradient id={`${mpn}-lcd-grad`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0d1721" />
              <stop offset="100%" stopColor="#081019" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={w} height={h} rx="3" fill={`url(#${mpn}-front-grad)`} stroke={bodyStroke} strokeWidth="1" />
          <rect x="0" y="0" width={w} height="1" rx="1" fill="var(--accent)" opacity="0.5" />
          <rect x="0" y="0" width="12" height={h} fill="#0a0b0d" />
          <rect x={w - 12} y="0" width="12" height={h} fill="#0a0b0d" />
          {[14, h / 2, h - 14].map((yy: number, i: number) => (
            <g key={i}>
              <circle cx="6" cy={yy} r="2.1" fill="#050607" stroke="#2a313a" strokeWidth="0.7" />
              <circle cx={w - 6} cy={yy} r="2.1" fill="#050607" stroke="#2a313a" strokeWidth="0.7" />
            </g>
          ))}
          {lcd && (
            <g transform="translate(18,8)">
              <rect x="0" y="0" width="30" height="16" rx="2" fill={`url(#${mpn}-lcd-grad)`} stroke="#34526a" strokeWidth="0.8" />
              <text x="15" y="6" textAnchor="middle" fontSize="2.6" fill="#7dd3fc" fontFamily="JetBrains Mono, monospace">UniFi</text>
              <text x="15" y="12" textAnchor="middle" fontSize="3.2" fill="#e2e8f0" fontFamily="JetBrains Mono, monospace" fontWeight="700">{(device as any)?.nameShort || "OK"}</text>
            </g>
          )}
          <text x={w / 2} y="8" textAnchor="middle" fontSize="4" fill="#9aa4b2" fontFamily="JetBrains Mono, monospace" fontWeight="700">{name}</text>
          {Array.from({ length: rj45Count }).map((_: unknown, i: number) => {
            const n = i + 1
            const row = is48 ? Math.floor(i / 24) : 0
            const col = is48 ? i % 24 : i
            const x = left + col * (portW + gap)
            const y = rowY + row * 14
            const conn = getConn(n)
            const isH = hovPort === n
            const isS = selPort === n
            const typeColor = TYPE_COLORS_SW[conn?.deviceType] || conn?.color || "var(--color-networking)"
            const online = conn ? !conn.offline : false
            const poeClass = getPoEClass(n)
            return (
              <g key={n} onMouseEnter={() => onHover?.(n)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(n)} style={{ cursor: "pointer" }}>
                <rect x={x} y={y} width={portW} height={portH} rx="1" fill={conn ? typeColor + "40" : "#141518"} stroke={isS ? "var(--accent)" : isH ? (conn ? typeColor : "var(--color-infra)") : "#3a3d46"} strokeWidth={isS ? 1.4 : isH ? 1 : 0.6} />
                <rect x={x + 3} y={y - 1.4} width="6" height="1.6" rx="0.5" fill={conn ? typeColor + "60" : "#1a1a1a"} />
                <rect x={x + 1.5} y={y + 2} width="9" height="4.5" rx="0.5" fill="#0b0d10" stroke="#1e232a" strokeWidth="0.35" />
                {conn && <circle cx={x + 10.1} cy={y + 1.5} r="2" fill={online ? "var(--ok)" : "var(--err)"}><animate attributeName="opacity" values="1;0.35;1" dur="2.8s" repeatCount="indefinite" /></circle>}
                {poeClass && <text x={x + 1.1} y={y + 8.2} fontSize="2" fill={poeClass === "++" ? "#f59e0b" : "#10b981"} fontFamily="JetBrains Mono, monospace" fontWeight="700">{poeClass}</text>}
                <text x={x + 6} y={y + 14} textAnchor="middle" fontSize="3.5" fill={conn ? typeColor : "#697280"} fontFamily="JetBrains Mono, monospace">{n}</text>
              </g>
            )
          })}
          {Array.from({ length: sfpCount + sfpPlusCount }).map((_: unknown, i: number) => {
            const n = rj45Count + i + 1
            const x = (is48 ? w - 66 : w - 42) + i * 14
            const y = is48 ? 27 : 20
            const conn = getConn(n)
            const isH = hovPort === n
            const isS = selPort === n
            const color = conn?.color || "var(--color-infra)"
            const isPlus = i >= sfpCount
            return (
              <g key={n} onMouseEnter={() => onHover?.(n)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(n)} style={{ cursor: "pointer" }}>
                <rect x={x} y={y} width="12" height="10" rx="1" fill={conn ? color + "18" : "#0a0a0c"} stroke={isS ? "var(--accent)" : isH ? (conn ? color : "var(--color-infra)") : "#2a2f37"} strokeWidth={isS ? 1.4 : isH ? 1 : 0.7} />
                <rect x={x + 2} y={y + 2} width="8" height="4" rx="0.5" fill="#161b22" />
                <text x={x + 6} y={y + 8.6} textAnchor="middle" fontSize="2" fill={isPlus ? "#e5e7eb" : "#9aa4b2"} fontFamily="JetBrains Mono, monospace">{isPlus ? "+" : "F"}</text>
                {conn && <circle cx={x + 10.2} cy={y + 1.6} r="0.9" fill={conn.offline ? "var(--err)" : "var(--ok)"}><animate attributeName="opacity" values="1;0.35;1" dur="2.5s" repeatCount="indefinite" /></circle>}
              </g>
            )
          })}
          <g transform={`translate(${w - 112},8)`}>
            {["SYS", "ETH", "SFP", depthClass === "pro" ? "RPS" : "PWR"].map((t: string, i: number) => (
              <g key={t} transform={`translate(${i * 24},0)`}>
                <circle cx="4" cy="4" r="1.8" fill="#059669"><animate attributeName="opacity" values="1;0.4;1" dur={`${2 + i * 0.4}s`} repeatCount="indefinite" /></circle>
                <text x="10" y="5.2" fontSize="2.3" fill="#7c8796" fontFamily="JetBrains Mono, monospace">{t}</text>
              </g>
            ))}
          </g>
        </g>
      )
    },
    renderBack: (w: number, h: number) => {
      const pro = depthClass === "pro"
      return (
        <g>
          <rect x="0" y="0" width={w} height={h} rx="3" fill="#111419" stroke="#242932" strokeWidth="1" />
          <rect x="0" y="0" width="12" height={h} fill="#0a0b0d" />
          <rect x={w - 12} y="0" width="12" height={h} fill="#0a0b0d" />
          <g transform="translate(20,8)">
            <rect x="0" y="0" width={w - 40} height={h - 16} rx="2" fill="#14181e" stroke="#2a313a" strokeWidth="0.8" />
            <rect x="12" y="12" width={pro ? 120 : 160} height={h - 40} fill="#0f1217" stroke="#2a313a" strokeWidth="0.7" />
            {Array.from({ length: pro ? 11 : 15 }).map((_: unknown, i: number) => (
              <rect key={i} x={18 + i * 10} y="18" width="6" height={h - 52} rx="0.8" fill="#242b34" />
            ))}
            <g transform={`translate(${w - (pro ? 170 : 140)},12)`}>
              <rect x="0" y="0" width="34" height="22" rx="1.2" fill="#0a0b0d" stroke="#2b3139" strokeWidth="0.8" />
              <text x="17" y="29" textAnchor="middle" fontSize="2.7" fill="#7f8a98" fontFamily="JetBrains Mono, monospace">AC</text>
              <rect x="46" y="0" width="24" height="14" rx="1" fill="#0a0b0d" stroke="#2b3139" strokeWidth="0.8" />
              <text x="58" y="23" textAnchor="middle" fontSize="2.5" fill="#7f8a98" fontFamily="JetBrains Mono, monospace">RESET</text>
            </g>
          </g>
        </g>
      )
    },
    getPortPosition: getFrontPortPosition,
  }
}

// Register UniFi switch models
DEVICE_MODULES["USW-Pro-48-POE"] = makeUniFiSwitchModule({ mpn: "USW-Pro-48-POE", name: "UniFi Pro 48 PoE", rj45Count: 48, sfpPlusCount: 4, poePlusPorts: 40, poePlusPlusPorts: 8, lcd: true, depthClass: "pro" })
DEVICE_MODULES["USW-Pro-24-POE"] = makeUniFiSwitchModule({ mpn: "USW-Pro-24-POE", name: "UniFi Pro 24 PoE", rj45Count: 24, sfpPlusCount: 2, poePlusPorts: 16, poePlusPlusPorts: 8, lcd: true, depthClass: "pro" })
DEVICE_MODULES["USW-48-POE"] = makeUniFiSwitchModule({ mpn: "USW-48-POE", name: "UniFi Standard 48 PoE", rj45Count: 48, sfpCount: 4, poePlusPorts: 32, lcd: true, depthClass: "standard" })
DEVICE_MODULES["USW-48"] = makeUniFiSwitchModule({ mpn: "USW-48", name: "UniFi Standard 48", rj45Count: 48, sfpCount: 4, lcd: true, depthClass: "standard" })
DEVICE_MODULES["USW-24"] = makeUniFiSwitchModule({ mpn: "USW-24", name: "UniFi Standard 24", rj45Count: 24, sfpCount: 2, lcd: true, depthClass: "standard" })
DEVICE_MODULES["USW-Enterprise-48-POE"] = makeUniFiSwitchModule({ mpn: "USW-Enterprise-48-POE", name: "UniFi Enterprise 48 PoE", rj45Count: 48, sfpPlusCount: 4, poePlusPorts: 48, lcd: true, depthClass: "pro" })
DEVICE_MODULES["USW-Pro-Max-24-POE"] = makeUniFiSwitchModule({ mpn: "USW-Pro-Max-24-POE", name: "UniFi Pro Max 24 PoE", rj45Count: 24, sfpPlusCount: 2, poePlusPlusPorts: 8, poePlusPorts: 16, lcd: true, depthClass: "pro", etherlighting: true })
DEVICE_MODULES["USW-Pro-Max-48-POE"] = makeUniFiSwitchModule({ mpn: "USW-Pro-Max-48-POE", name: "UniFi Pro Max 48 PoE", rj45Count: 48, sfpPlusCount: 4, poePlusPorts: 32, poePlusPlusPorts: 16, lcd: true, depthClass: "pro", etherlighting: true })

// ── PATCH PANEL FACTORY ──

function makePatchPanelModule({ mpn, portCount = 24, sizeU = 1, brand = "PATCH PANEL", rearType = "110" }: { mpn: string; portCount?: number; sizeU?: number; brand?: string; rearType?: "110" | "keystone" }): DeviceModule {
  const cols = portCount >= 48 ? 24 : 12
  const panelLeft = 16
  const panelTop = 14
  const portW = 10
  const portH = 9
  const gapX = 5
  const gapY = 12

  const renderRj45 = (n: number, x: number, y: number, conn: any, hovPort: any, selPort: any, onHover: any, onClick: any) => {
    const isH = hovPort === n
    const isS = selPort === n
    const color = conn ? (TYPE_COLORS_SW[conn?.deviceType] || conn?.color || "var(--color-networking)") : "var(--color-networking)"
    return (
      <g key={n} onMouseEnter={() => onHover?.(n)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(n)} style={{ cursor: "pointer" }}>
        <title>{conn?.label || `Port ${n}`}</title>
        <rect x={x} y={y} width={portW} height={portH - 0.5} rx="1" fill={conn ? color + "18" : "#0a0a0c"} stroke={isS ? "var(--accent)" : isH ? (conn ? color : "var(--color-infra)") : "#242932"} strokeWidth={isS ? 1.4 : isH ? 1 : 0.6} />
        <rect x={x + 2.6} y={y - 1.2} width={portW - 5.2} height="1.6" rx="0.5" fill={conn ? color + "40" : "#1a1a1a"} />
        <rect x={x + 1.6} y={y + 2} width={portW - 3.2} height="4.2" rx="0.5" fill="#0b0d10" stroke="#1e232a" strokeWidth="0.35" />
        {conn && (
          <circle cx={x + portW - 1.7} cy={y + 1.4} r="0.9" fill={conn.offline ? "var(--err)" : "var(--ok)"}>
            <animate attributeName="opacity" values="1;0.35;1" dur="2.8s" repeatCount="indefinite" />
          </circle>
        )}
        <text x={x + portW / 2} y={y + portH + 3.5} textAnchor="middle" fontSize="2.3" fill={conn ? color : "#697280"} fontFamily="JetBrains Mono, monospace">{n}</text>
      </g>
    )
  }

  return {
    name: `${brand} ${portCount}-Port Patch Panel`,
    sizeU,
    frontPorts: portCount,
    rearPorts: portCount,
    renderFront: (w: number, h: number, { device, ports = {}, hovPort, selPort, onHover, onClick }: DeviceModuleRenderProps) => (
      <g>
        <defs>
          <linearGradient id={`${mpn}-front-grad`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#17191d" />
            <stop offset="100%" stopColor="#0b0c10" />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width={w} height={h} rx="3" fill={`url(#${mpn}-front-grad)`} stroke="#28303a" strokeWidth="1" />
        <rect x="0" y="0" width={w} height="1" rx="1" fill="var(--accent)" opacity="0.5" />
        <rect x="0" y="0" width="12" height={h} fill="#0a0b0d" />
        <rect x={w - 12} y="0" width="12" height={h} fill="#0a0b0d" />
        {[14, h / 2, h - 14].map((yy: number, i: number) => (
          <g key={i}>
            <circle cx="6" cy={yy} r="2.1" fill="#050607" stroke="#2a313a" strokeWidth="0.7" />
            <circle cx={w - 6} cy={yy} r="2.1" fill="#050607" stroke="#2a313a" strokeWidth="0.7" />
          </g>
        ))}
        <text x={w / 2} y="8" textAnchor="middle" fontSize="4.2" fill="#9aa4b2" fontFamily="JetBrains Mono, monospace" fontWeight="700">{brand}</text>
        {Array.from({ length: portCount }).map((_: unknown, i: number) => {
          const n = i + 1
          const row = Math.floor(i / cols)
          const col = i % cols
          const x = panelLeft + col * (portW + gapX)
          const y = panelTop + row * gapY
          const conn = (ports as any)?.[n] || (device as any)?.ports?.[n] || null
          return renderRj45(n, x, y, conn, hovPort, selPort, onHover, onClick)
        })}
      </g>
    ),
    renderBack: (w: number, h: number) => (
      <g>
        <rect x="0" y="0" width={w} height={h} rx="3" fill="#111419" stroke="#28303a" strokeWidth="1" />
        <text x={w / 2} y="8" textAnchor="middle" fontSize="4" fill="#9aa4b2" fontFamily="JetBrains Mono, monospace" fontWeight="700">{brand} REAR</text>
        {Array.from({ length: portCount }).map((_: unknown, i: number) => {
          const n = i + 1
          const row = Math.floor(i / cols)
          const col = i % cols
          const x = 18 + col * 16
          const y = 16 + row * 14
          return (
            <g key={n}>
              <rect x={x} y={y} width="12" height="8" rx="0.8" fill="#101318" stroke="#2a313a" strokeWidth="0.6" />
              {rearType === "110" ? (<>
                <rect x={x + 1.5} y={y + 1.5} width="9" height="2" rx="0.6" fill="#91672c" />
                <rect x={x + 1.5} y={y + 4.5} width="9" height="2" rx="0.6" fill="#91672c" />
              </>) : (
                <rect x={x + 2} y={y + 1.5} width="8" height="5" rx="0.8" fill="#1d2229" stroke="#2a313a" strokeWidth="0.4" />
              )}
              <text x={x + 6} y={y + 12} textAnchor="middle" fontSize="2.2" fill="#707b89" fontFamily="JetBrains Mono, monospace">{n}</text>
            </g>
          )
        })}
        <rect x="26" y={h - 10} width={w - 52} height="3" rx="1.5" fill="#1e232a" />
      </g>
    ),
    getPortPosition: (portNum: number) => {
      const idx = portNum - 1
      const row = Math.floor(idx / cols)
      const col = idx % cols
      return { x: panelLeft + col * (portW + gapX) + portW / 2, y: panelTop + row * gapY + portH / 2 }
    },
  }
}

// Register patch panels
DEVICE_MODULES["PATCH-24-GENERIC"] = makePatchPanelModule({ mpn: "PATCH-24-GENERIC", portCount: 24, brand: "PATCH PANEL" })
DEVICE_MODULES["PATCH-48-GENERIC"] = makePatchPanelModule({ mpn: "PATCH-48-GENERIC", portCount: 48, sizeU: 2, brand: "PATCH PANEL" })

// ── UNIFI GATEWAY FACTORY ──

function makeUniFiGatewayModule({ mpn, name, wanRj45 = 1, wan25g = false, lanRj45 = 8, sfpPlus = 2, hddBay = true, poeCount = 0 }: { mpn: string; name: string; wanRj45?: number; wan25g?: boolean; lanRj45?: number; sfpPlus?: number; hddBay?: boolean; poeCount?: number }): DeviceModule {
  const renderRj45Port = (n: number, x: number, y: number, conn: any, hovPort: any, selPort: any, onHover: any, onClick: any) => {
    const isH = hovPort === n
    const isS = selPort === n
    const color = conn ? (TYPE_COLORS_SW[conn?.deviceType] || conn?.color || "var(--color-networking)") : "var(--color-networking)"
    return (
      <g key={n} onMouseEnter={() => onHover?.(n)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(n)} style={{ cursor: "pointer" }}>
        <title>{conn?.label || `Port ${n}`}</title>
        <rect x={x} y={y} width="12" height="9.5" rx="1" fill={conn ? color + "18" : "#0a0a0c"} stroke={isS ? "var(--accent)" : isH ? (conn ? color : "var(--color-infra)") : "#242932"} strokeWidth={isS ? 1.4 : isH ? 1 : 0.6} />
        <rect x={x + 2.6} y={y - 1.2} width="6.8" height="1.6" rx="0.5" fill={conn ? color + "40" : "#1a1a1a"} />
        <rect x={x + 1.6} y={y + 2} width="8.8" height="4.2" rx="0.5" fill="#0b0d10" stroke="#1e232a" strokeWidth="0.35" />
        {conn && (
          <circle cx={x + 10.3} cy={y + 1.4} r="0.9" fill={conn.offline ? "var(--err)" : "var(--ok)"}>
            <animate attributeName="opacity" values="1;0.35;1" dur="2.8s" repeatCount="indefinite" />
          </circle>
        )}
        <text x={x + 6} y={y + 13.5} textAnchor="middle" fontSize="2.3" fill={conn ? color : "#697280"} fontFamily="JetBrains Mono, monospace">{n}</text>
      </g>
    )
  }

  const renderSfpPort = (n: number, x: number, y: number, conn: any, hovPort: any, selPort: any, onHover: any, onClick: any) => {
    const isH = hovPort === n
    const isS = selPort === n
    const color = conn ? (TYPE_COLORS_SW[conn?.deviceType] || conn?.color || "var(--color-infra)") : "var(--color-infra)"
    return (
      <g key={n} onMouseEnter={() => onHover?.(n)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(n)} style={{ cursor: "pointer" }}>
        <title>{conn?.label || `SFP+ ${n}`}</title>
        <rect x={x} y={y} width="12" height="10" rx="1" fill={conn ? color + "18" : "#0a0a0c"} stroke={isS ? "var(--accent)" : isH ? (conn ? color : "var(--color-infra)") : "#2a2f37"} strokeWidth={isS ? 1.4 : isH ? 1 : 0.7} />
        <rect x={x + 2} y={y + 2} width="8" height="4" rx="0.5" fill="#161b22" />
        <text x={x + 6} y={y + 8.4} textAnchor="middle" fontSize="2" fill="#e5e7eb" fontFamily="JetBrains Mono, monospace">+</text>
        {conn && (
          <circle cx={x + 10.2} cy={y + 1.5} r="0.9" fill={conn.offline ? "var(--err)" : "var(--ok)"}>
            <animate attributeName="opacity" values="1;0.35;1" dur="2.8s" repeatCount="indefinite" />
          </circle>
        )}
      </g>
    )
  }

  return {
    name, sizeU: 1, frontPorts: wanRj45 + lanRj45 + sfpPlus, rearPorts: 2,
    renderFront: (w: number, h: number, { device, ports = {}, hovPort, selPort, onHover, onClick }: DeviceModuleRenderProps) => {
      const lcdText = (device as any)?.nameShort || "UniFi"
      const left = 74
      const portW = 12
      const gap = 3.2
      const getConn = (n: number) => (ports as any)?.[n] || (device as any)?.ports?.[n] || null
      return (
        <g>
          <defs>
            <linearGradient id={`${mpn}-grad`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#17191d" /><stop offset="100%" stopColor="#0b0c10" /></linearGradient>
            <linearGradient id={`${mpn}-lcd`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0d1721" /><stop offset="100%" stopColor="#081019" /></linearGradient>
          </defs>
          <rect x="0" y="0" width={w} height={h} rx="3" fill={`url(#${mpn}-grad)`} stroke="#28303a" strokeWidth="1" />
          <rect x="0" y="0" width={w} height="1" rx="1" fill="var(--accent)" opacity="0.5" />
          <rect x="18" y="8" width="30" height="16" rx="2" fill={`url(#${mpn}-lcd)`} stroke="#34526a" strokeWidth="0.8" />
          <text x="33" y="14" textAnchor="middle" fontSize="2.8" fill="#7dd3fc" fontFamily="JetBrains Mono, monospace">UniFi OS</text>
          <text x="33" y="20" textAnchor="middle" fontSize="3.2" fill="#e2e8f0" fontFamily="JetBrains Mono, monospace" fontWeight="700">{lcdText}</text>
          <text x={w / 2} y="8" textAnchor="middle" fontSize="4" fill="#9aa4b2" fontFamily="JetBrains Mono, monospace" fontWeight="700">{name}</text>
          {hddBay && (
            <g transform={`translate(${w - 68},10)`}>
              <rect x="0" y="0" width="42" height="20" rx="1.5" fill="#0c0e12" stroke="#2a313a" strokeWidth="0.8" />
              <rect x="4" y="3" width="34" height="14" rx="1.2" fill="#141920" stroke="#20262d" strokeWidth="0.5" />
              <circle cx="35.5" cy="5.5" r="0.9" fill="#3b82f6"><animate attributeName="opacity" values="1;0.3;1" dur="2.4s" repeatCount="indefinite" /></circle>
            </g>
          )}
          {Array.from({ length: wanRj45 + lanRj45 }).map((_: unknown, i: number) => {
            const n = i + 1
            const x = left + i * (portW + gap)
            const y = 18
            const conn = getConn(n)
            return (
              <g key={n}>
                {renderRj45Port(n, x, y, conn, hovPort, selPort, onHover, onClick)}
                {i < wanRj45 && <text x={x + 6} y="15" textAnchor="middle" fontSize="2.2" fill={wan25g ? "#f59e0b" : "#7f8a98"} fontFamily="JetBrains Mono, monospace">WAN</text>}
                {i >= wanRj45 && <text x={x + 6} y="15" textAnchor="middle" fontSize="2.2" fill={i - wanRj45 < poeCount ? "#10b981" : "#7f8a98"} fontFamily="JetBrains Mono, monospace">{i - wanRj45 + 1}</text>}
              </g>
            )
          })}
          {Array.from({ length: sfpPlus }).map((_: unknown, i: number) => {
            const n = wanRj45 + lanRj45 + i + 1
            const x = left + (wanRj45 + lanRj45) * (portW + gap) + 10 + i * 16
            const conn = getConn(n)
            return (
              <g key={n}>
                {renderSfpPort(n, x, 18, conn, hovPort, selPort, onHover, onClick)}
                <text x={x + 6} y="15" textAnchor="middle" fontSize="2.1" fill="#d1d5db" fontFamily="JetBrains Mono, monospace">10G</text>
              </g>
            )
          })}
        </g>
      )
    },
    renderBack: (w: number, h: number) => (
      <g>
        <rect x="0" y="0" width={w} height={h} rx="3" fill="#111419" stroke="#242932" strokeWidth="1" />
        <g transform="translate(20,8)">
          <rect x="0" y="0" width={w - 40} height={h - 16} rx="2" fill="#14181e" stroke="#2a313a" strokeWidth="0.8" />
          <rect x={w - 110} y="14" width="34" height="20" rx="1.2" fill="#0a0b0d" stroke="#2b3139" strokeWidth="0.8" />
          <text x={w - 93} y="41" textAnchor="middle" fontSize="2.7" fill="#7f8a98" fontFamily="JetBrains Mono, monospace">AC</text>
        </g>
      </g>
    ),
    getPortPosition: (portNum: number, _w: number) => {
      const left = 74
      const portW = 12
      const gap = 3.2
      const totalRj = wanRj45 + lanRj45
      if (portNum <= totalRj) return { x: left + (portNum - 1) * (portW + gap) + 6, y: 22.75 }
      const sfpIdx = portNum - totalRj - 1
      return { x: left + totalRj * (portW + gap) + 10 + sfpIdx * 16 + 6, y: 23 }
    },
  }
}

// Register UniFi gateways
DEVICE_MODULES["UDM-Pro"] = makeUniFiGatewayModule({ mpn: "UDM-Pro", name: "UniFi Dream Machine Pro", wanRj45: 1, lanRj45: 8, sfpPlus: 2, hddBay: true })
DEVICE_MODULES["UDM-SE"] = makeUniFiGatewayModule({ mpn: "UDM-SE", name: "UniFi Dream Machine SE", wanRj45: 1, wan25g: true, lanRj45: 8, sfpPlus: 2, hddBay: true, poeCount: 8 })

// ── APC UPS FACTORY ──

function makeApcUpsModule({ mpn, name, rearOutlets, inputLabel, rearOutletType = "nema", simpler = false }: { mpn: string; name: string; rearOutlets: number; inputLabel: string; rearOutletType?: "nema" | "iec"; simpler?: boolean }): DeviceModule {
  return {
    name, sizeU: 2, frontPorts: 0, rearPorts: rearOutlets,
    renderFront: (w: number, h: number, { device }: DeviceModuleRenderProps) => {
      const loadPct = Math.max(0, Math.min(100, (device as any)?.loadPct ?? 42))
      const runtimeMin = (device as any)?.runtimeMin ?? 37
      const battBars = Math.max(0, Math.min(5, Math.round(((device as any)?.batteryPct ?? 100) / 20)))
      const loadBars = Math.max(0, Math.min(5, Math.ceil(loadPct / 20)))
      const onBattery = (device as any)?.onBattery === true
      const status = device?.status || (onBattery ? "BAT" : "ONLINE")
      const statusColor = onBattery ? "var(--color-power)" : status === "FAULT" ? "var(--err)" : "var(--ok)"
      return (
        <g>
          <defs>
            <linearGradient id={`${mpn}-body`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#17191d" /><stop offset="100%" stopColor="#0c0d10" /></linearGradient>
            <linearGradient id={`${mpn}-lcd`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0b1620" /><stop offset="100%" stopColor="#081018" /></linearGradient>
          </defs>
          <rect x="0" y="0" width={w} height={h} rx="4" fill={`url(#${mpn}-body)`} stroke="#262b33" strokeWidth="1" />
          <rect x="0" y="0" width={w} height="1" rx="1" fill="var(--accent)" opacity="0.5" />
          <g transform={`translate(${(w - 300) / 2}, 8)`}>
            <rect x="0" y="0" width="300" height={h - 16} rx="3" fill="#14171c" stroke="#2b3139" strokeWidth="0.9" />
            <rect x="8" y="8" width={simpler ? 64 : 78} height={h - 32} rx="3" fill={`url(#${mpn}-lcd)`} stroke="#375166" strokeWidth="0.8" />
            <text x="16" y="18" fontSize="4" fill="#7dd3fc" fontFamily="JetBrains Mono, monospace">UPS</text>
            <text x="16" y="31" fontSize="10" fill="#dbeafe" fontFamily="JetBrains Mono, monospace" fontWeight="700">{loadPct}%</text>
            <text x="16" y="39" fontSize="4" fill="#8fb6d9" fontFamily="JetBrains Mono, monospace">LOAD</text>
            <text x="16" y="54" fontSize="8" fill="#dbeafe" fontFamily="JetBrains Mono, monospace" fontWeight="700">{runtimeMin}m</text>
            <text x="16" y="61" fontSize="4" fill="#8fb6d9" fontFamily="JetBrains Mono, monospace">RUNTIME</text>
            <text x="16" y="74" fontSize="4.2" fill={statusColor} fontFamily="JetBrains Mono, monospace" fontWeight="700">{status}</text>
            <circle cx={simpler ? 96 : 112} cy={(h - 16) / 2} r="10" fill="#0c0e12" stroke={onBattery ? "#d97706" : "#2c7a54"} strokeWidth="1.2" />
            <g transform={`translate(${simpler ? 126 : 140},18)`}>
              <text x="0" y="0" fontSize="3.5" fill="#7f8a98" fontFamily="JetBrains Mono, monospace">BAT</text>
              {Array.from({ length: 5 }).map((_: unknown, i: number) => (
                <rect key={`bat-${i}`} x={i * 10} y="6" width="7" height="12" rx="1" fill={i < battBars ? "#059669" : "#1d2229"} stroke="#2b3139" strokeWidth="0.6" />
              ))}
              <text x="0" y="30" fontSize="3.5" fill="#7f8a98" fontFamily="JetBrains Mono, monospace">LOAD</text>
              {Array.from({ length: 5 }).map((_: unknown, i: number) => (
                <rect key={`load-${i}`} x={i * 10} y="36" width="7" height="12" rx="1" fill={i < loadBars ? (loadPct > 80 ? "#d97706" : "#059669") : "#1d2229"} stroke="#2b3139" strokeWidth="0.6" />
              ))}
            </g>
            <text x="292" y={h - 24} textAnchor="end" fontSize="4.8" fill="#8cc63f" opacity="0.85" fontFamily="JetBrains Mono, monospace" fontWeight="700">APC</text>
          </g>
        </g>
      )
    },
    renderBack: (w: number, h: number, { device, ports = {}, hovPort, selPort, onHover, onClick }: DeviceModuleRenderProps) => {
      const outlet = (n: number, x: number, y: number) => {
        const conn = (ports as any)?.[n] || (device as any)?.ports?.[n] || null
        const isH = hovPort === n
        const isS = selPort === n
        const color = conn ? (TYPE_COLORS_SW[conn?.deviceType] || conn?.color || "var(--color-networking)") : "var(--color-networking)"
        return (
          <g key={n} onMouseEnter={() => onHover?.(n)} onMouseLeave={() => onHover?.(null)} onClick={() => onClick?.(n)} style={{ cursor: "pointer" }}>
            <rect x={x} y={y} width={rearOutletType === "iec" ? 16 : 18} height="12" rx="1.2" fill={conn ? color + "18" : "#0a0a0c"} stroke={isS ? "var(--accent)" : isH ? (conn ? color : "var(--color-infra)") : "#2a2f37"} strokeWidth={isS ? 1.4 : isH ? 1 : 0.8} />
            {rearOutletType === "iec" ? (<>
              <rect x={x + 3} y={y + 2.2} width="3" height="6" rx="0.8" fill="#1d2229" />
              <rect x={x + 10} y={y + 2.2} width="3" height="6" rx="0.8" fill="#1d2229" />
            </>) : (<>
              <rect x={x + 3.5} y={y + 2.5} width="2" height="5" rx="0.6" fill="#1d2229" />
              <rect x={x + 12.5} y={y + 2.5} width="2" height="5" rx="0.6" fill="#1d2229" />
              <rect x={x + 7.5} y={y + 7} width="3" height="2" rx="0.6" fill="#1d2229" />
            </>)}
            {conn && <circle cx={x + (rearOutletType === "iec" ? 13 : 15)} cy={y + 1.7} r="0.9" fill={conn.offline ? "var(--err)" : "var(--ok)"}><animate attributeName="opacity" values="1;0.3;1" dur="2.5s" repeatCount="indefinite" /></circle>}
            <text x={x + (rearOutletType === "iec" ? 8 : 9)} y={y + 16} textAnchor="middle" fontSize="2.5" fill={conn ? color : "#68707c"} fontFamily="JetBrains Mono, monospace">{n}</text>
          </g>
        )
      }
      const cols = rearOutletType === "iec" ? (rearOutlets > 8 ? 4 : 3) : 3
      return (
        <g>
          <rect x="0" y="0" width={w} height={h} rx="4" fill="#111419" stroke="#242932" strokeWidth="1" />
          <g transform={`translate(${(w - 320) / 2}, 8)`}>
            <rect x="0" y="0" width="320" height={h - 16} rx="3" fill="#15191f" stroke="#2b3139" strokeWidth="0.9" />
            <rect x="8" y="50" width="24" height="6" rx="1" fill="#0a0b0d" stroke="#7f1d1d" strokeWidth="0.8" />
            <text x="20" y="55" textAnchor="middle" fontSize="2.5" fill="#ef4444" fontFamily="JetBrains Mono, monospace">EPO</text>
            <rect x="40" y="50" width="22" height="8" rx="1" fill="#111319" stroke="#2d3641" strokeWidth="0.7" />
            <text x="51" y="56" textAnchor="middle" fontSize="2.3" fill="#9aa4b2" fontFamily="JetBrains Mono, monospace">{inputLabel}</text>
            {Array.from({ length: rearOutlets }).map((_: unknown, i: number) => {
              const x = 110 + (i % cols) * 24
              const y = 16 + Math.floor(i / cols) * 24
              return outlet(i + 1, x, y)
            })}
          </g>
        </g>
      )
    },
    getPortPosition: (portNum: number, w: number) => {
      const baseX = (w - 320) / 2
      const cols = rearOutletType === "iec" ? (rearOutlets > 8 ? 4 : 3) : 3
      const idx = portNum - 1
      return { x: baseX + 110 + (idx % cols) * 24 + (rearOutletType === "iec" ? 8 : 9), y: 8 + 16 + Math.floor(idx / cols) * 24 + 6 }
    },
  }
}

// Register APC UPS models
DEVICE_MODULES["APC-SRT2200VA"] = makeApcUpsModule({ mpn: "APC-SRT2200VA", name: "APC Smart-UPS SRT2200VA", rearOutlets: 10, inputLabel: "C20", rearOutletType: "iec" })
DEVICE_MODULES["APC-SMT1500RM2UC"] = makeApcUpsModule({ mpn: "APC-SMT1500RM2UC", name: "APC Smart-UPS SMT1500RM2UC", rearOutlets: 6, inputLabel: "NEMA", rearOutletType: "nema", simpler: true })

// ── GENERIC FALLBACK MODULE ──
DEVICE_MODULES['_generic'] = {
    renderFront: (w, h, { device }) => (
      <g>
        <defs>
          <linearGradient id={`gen-${device?.id || 'default'}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e2024" />
            <stop offset="100%" stopColor="#161820" />
          </linearGradient>
        </defs>
        <rect x={0} y={0} width={w} height={h} rx={2} fill={`url(#gen-${device?.id || 'default'})`} stroke="#3a3d46" strokeWidth={0.5} />
        <rect x={0} y={0} width={w} height={1} fill="var(--accent)" opacity={0.5} rx={1} />
        <circle cx={12} cy={h / 2} r={2} fill={device?.status === 'online' || device?.status === 'active' ? 'var(--ok)' : 'var(--err)'}>
          <animate attributeName="opacity" values="1;0.4;1" dur="3s" repeatCount="indefinite" />
        </circle>
        <text x={22} y={h / 2 - 3} fontSize={5} fill="var(--text-1)" fontFamily="var(--font-mono)" fontWeight={700}>{device?.name}</text>
        <text x={22} y={h / 2 + 5} fontSize={4} fill="var(--text-3)" fontFamily="var(--font-mono)">{device?.model}</text>
        {device?.ip && <text x={w - 10} y={h / 2 + 2} textAnchor="end" fontSize={3.5} fill="#3a3d46" fontFamily="var(--font-mono)">{device.ip}</text>}
      </g>
    ),
    renderBack: (w, h, { device }) => (
      <g>
        <rect x={0} y={0} width={w} height={h} rx={2} fill="#111" stroke="#2a2d35" strokeWidth={0.5} />
        <text x={w / 2} y={h / 2 + 2} textAnchor="middle" fontSize={4} fill="#2a2d35" fontFamily="var(--font-mono)">{device?.model} — rear</text>
      </g>
    ),
}

export function getDeviceModule(model: string | null | undefined): DeviceModule {
  if (!model) return DEVICE_MODULES['_generic']
  // Exact match first
  if (DEVICE_MODULES[model]) return DEVICE_MODULES[model]
  // Uppercase match
  const upper = model.toUpperCase().replace(/\s+/g, '-')
  const match = Object.keys(DEVICE_MODULES).find(k => k.toUpperCase() === upper)
  if (match) return DEVICE_MODULES[match]
  // Partial match (model contains key or key contains model)
  const partial = Object.keys(DEVICE_MODULES).find(k =>
    k !== '_generic' && (upper.includes(k.toUpperCase()) || k.toUpperCase().includes(upper))
  )
  if (partial) return DEVICE_MODULES[partial]
  return DEVICE_MODULES['_generic']
}

export { TYPE_COLORS }
